// app/javascript/controllers/validation_controller.js
import { Controller } from "@hotwired/stimulus"

// Validation rules for extending HTML validation
import { rules } from "@/lib/validation_rules"

export default class extends Controller {
  static targets = ["field"]
  static values = {
    validateOnInput: { type: Boolean, default: true },
    validateOnBlur: { type: Boolean, default: true }
  }

  connect() {
    this.rules = rules
    this.element.noValidate = true
    this.touched = new Set()
    this.attemptedSubmit = false
  }

  validateField({ target: field }) {
    // Clear any existing errors
    this.clearError(field)

    // First check HTML validity
    let isValid = field.checkValidity()
    let errorMessage = field.validationMessage

    // If HTML validation passes, check for additional rules
    if (isValid && this.hasCustomRules(field)) {
      const result = this.validateCustomRules(field)
      isValid = result.isValid
      errorMessage = result.message
    }

    if (!isValid) {
      this.showError(field, errorMessage)
      return false
    }

    // Show success state if field has been touched
    if (this.touched.has(field) && this.attemptedSubmit) {
      field.classList.add("is-valid")
    }

    return true
  }

  validate(event) {
    let isValid = true
    this.attemptedSubmit = true // Mark that submission was attempted
    this.touched.clear() // Reset touched fields on form submit

    this.fieldTargets.forEach(field => {
      this.touched.add(field)
      if (!this.validateField({ target: field })) {
        isValid = false
      }
    })

    if (!isValid) {
      event.stopImmediatePropagation() // Prevent Stimulus from firing additional actions
      event.preventDefault()
      this.focusFirstInvalid()
    }
  }

  handleInput({ target: field }) {
    if (this.validateOnInputValue && this.attemptedSubmit && this.touched.has(field)) {
      this.validateField({ target: field })
    }
  }

  handleBlur({ target: field }) {
    if (this.validateOnBlurValue && this.attemptedSubmit) {
      this.touched.add(field)
      this.validateField({ target: field })
    }
  }

  // Private methods

  hasCustomRules(field) {
    return field.dataset.validates || field.dataset.validationRules
  }

  validateCustomRules(field) {
    let rules = {}

    // Check for inline rules
    if (field.dataset.validates) {
      try {
        rules = { ...rules, ...JSON.parse(field.dataset.validates) }
      } catch (e) {
        console.warn("Invalid validation rules:", field.dataset.validates)
      }
    }

    // Check for predefined rule set
    if (field.dataset.validationRules) {
      rules = { ...rules, ...this.rules[field.dataset.validationRules] }
    }

    // Run custom validations
    for (const [rule, config] of Object.entries(rules)) {
      const validator = this.validators[rule]
      if (!validator) continue

      const error = validator(field.value, config)
      if (error) {
        return { isValid: false, message: error }
      }
    }

    return { isValid: true, message: null }
  }

  showError(field, message) {
    field.classList.remove("is-valid")
    field.classList.add("is-invalid")

    let errorContainer = this.findErrorContainer(field)
    if (!errorContainer) {
      errorContainer = this.createErrorContainer(field)
    }

    errorContainer.textContent = message
    errorContainer.classList.remove("d-none")
  }

  clearError(field) {
    field.classList.remove("is-invalid", "is-valid")
    const errorContainer = this.findErrorContainer(field)
    if (errorContainer) {
      errorContainer.textContent = ""
      errorContainer.classList.add("d-none")
    }
  }

  findErrorContainer(field) {
    const containerId = field.getAttribute("aria-describedby")
    return containerId ?
      document.getElementById(containerId) :
      field.nextElementSibling?.classList.contains("invalid-feedback") ?
        field.nextElementSibling : null
  }

  createErrorContainer(field) {
    const container = document.createElement("div")
    container.classList.add("invalid-feedback")
    const id = `${field.id || field.name}_error`
    container.id = id
    field.setAttribute("aria-describedby", id)
    field.parentNode.insertBefore(container, field.nextSibling)
    return container
  }

  focusFirstInvalid() {
    this.fieldTargets.find(f => !f.checkValidity())?.focus()
  }

  // Validator registry - extend HTML's built-in validation
  validators = {
    async: async (value, { url, method = "POST" }) => {
      const response = await fetch(url, {
        method,
        body: JSON.stringify({ value }),
        headers: { "Content-Type": "application/json" }
      })
      if (!response.ok) {
        return "Validation failed"
      }
    },

    matches: (value, selector) => {
      const otherField = document.querySelector(selector)
      return value === otherField?.value ? null : "Fields must match"
    },

    email: (value) => {
      return /\S+@\S+\.\S+/.test(value) ? null : "Invalid email address"
    },

    // Allows strings to be converted to numbers and validated with min, not avaialable in default HTML validation
    minAmount: (value, min) => {
      return parseFloat(value) >= min ? null : `The minimum amount is ${min}.00`
    },

    // Allows strings to be converted to numbers and validated with max, not avaialable in default HTML validation
    maxAmount: (value, max) => {
      return parseFloat(value) <= max ? null : `The maximum amount is ${max}.00`
    },

    minlength: (value, length) => {
      return value.length >= length ? null : `Must be at least ${length} characters`
    },

    maxlength: (value, length) => {
      return value.length <= length ? null : `Must be at most ${length} characters`
    },

    // Add more custom validators as needed
  }
}
